/* Built In Imports */
import { useRouter } from 'next/router';
import { useRef } from 'react';

/* External Imports */
import { Box, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';

/* Internal Imports */
/* Components */
import SingleLineText from '@components/UI/SingleLineText';
import { ResearchIntroSt } from '@ieo/Common/CompLangConfig';
import LazyImageRenderComponent from '../../components/UI/LazyImageRenderComponent';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import IeoBenefitsV8Modal from '../../landing_pages/ieo/Sections/IeoBenefitsV8Modal';

/**
 * @param {Object} sectionContent
 * @param {String} region
 * @param {String} lang
 * @returns {ReactElement} Research Intro
 */
const ResearchIntro = ({
  sectionContent,
  region,
  lang,
  pageData,
  pageName,
  prgData,
}) => {
  const btnRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();
  function findField(fieldId) {
    const data = sectionContent.find(el => el.fieldId === fieldId);
    return data;
  }

  return (
    <Box bg={`#F1EBDF`} bgRepeat={'no-repeat'} w="100%">
      <Box
        w="100%"
        h="100%"
        maxW={'1180px'}
        mx="auto"
        pos="relative"
        id="research-intro"
        pt={{ base: '0', md: '0' }}
      >
        <Box
          maxW={{
            base: '100%',
            sm: 'calc(100% - 66px)',
            xl: '1180px',
          }}
          w="100%"
          px={{ base: '0px', md: '0px' }}
          ml={{ base: '0', sm: '30px', md: '16px', lg: '50px', xl: 'auto' }}
          mr={{ base: '0px', sm: 'auto', xl: 'auto' }}
        >
          <Flex
            mx="auto"
            w="100%"
            alignItems={{ base: 'start', xl: 'center' }}
            flexWrap="wrap"
            px={{ base: '16px', sm: '0', md: '10px', lg: '0' }}
            pt={{ base: '60px', md: '80px', lg: '97px' }}
            pb={{ base: '40px', md: 0 }}
          >
            <Box width="100%">
              <Flex
                flexDir={{
                  base: 'column',
                  xl: 'row',
                }}
                height={
                  sectionContent[1]?._modelApiKey === 'json_table'
                    ? { base: '90px', md: '140px', xl: 'auto' }
                    : 'auto'
                }
                width="100%"
              >
                <SingleLineText
                  my="0"
                  mt={{ base: '0', lg: '9px' }}
                  mb={{ base: '0', lg: '9px' }}
                  sectionContent={findField('take-charge')}
                  region={region}
                  lang={lang}
                  {...ResearchIntroSt[lang]['wavesOfBliss']}
                />
              </Flex>

              <StructuredTextParser
                str={render(findField('with-sadhguru').body)}
                lang={lang}
                fontFamily={'FedraSansStd-Book'}
                pt={{ base: '10px', md: '20px' }}
                color={'#12166F'}
                fontSize={{
                  base: '20px',
                  md: '24px',
                  lg: '30px',
                }}
                lineHeight={{ base: '25px', md: '32px', lg: '38px' }}
                fontWeight="400"
                maxWidth={{
                  base: '100%',
                  md: '100%',
                  lg: '900px',
                }}
                className="with-sadhguru-sub-text"
                m="0px !important"
              />
              {findField('with-sadhguru-2') && (
                <Box lang={lang} weight="400" color="#3B444B" my="10px">
                  {findField('with-sadhguru-2').text}
                </Box>
              )}
            </Box>
          </Flex>
          <Flex
            flexDir={{ base: 'column', md: 'row' }}
            pos="relative"
            top={{ base: '0', md: '0', xl: '30px' }}
            mt={{ base: '0', md: '60px', xl: '30px' }}
            alignItems={{ base: 'flex-start', md: 'center' }}
          >
            <LazyImageRenderComponent
              src={sectionContent[2]?.thumbnailDesktopImage?.url}
              h={{ base: 'auto', md: 'auto', xl: 'auto' }}
              maxH={{ base: 'auto', md: '310px', lg: '310px', xl: '410px' }}
              maxW={{ base: '100%', md: '330px', lg: '530px', xl: '730px' }}
              objectFit="contain"
            />
            <Box width="100%">
              <Flex
                flexDir="column"
                mx={{ base: '16px', sm: '0', md: '0px' }}
                pl={{ base: '0px', md: '40px' }}
                pr={{ base: '0px', md: '0px' }}
                mb={{ base: '54px', md: '0' }}
                alignItems="start"
                minW={{ base: 'calc(100% - 32px)', md: '100%' }}
                width={{ base: 'calc(100% - 32px)', md: '100%' }}
                maxW={{ base: 'calc(100% - 32px)', md: '540px', lg: '490px' }}
                pt={{ base: '30px', md: '0px' }}
                pb={{ base: '', md: '30px' }}
              >
                <SingleLineText
                  sectionContent={findField('research-shows-title')}
                  my="0"
                  fontSize={{ base: '22px', md: '28px' }}
                  lineHeight={{ base: '26.4px', md: '30.8px' }}
                  color="#12166F"
                  fontFamily="FedraSansStd-medium"
                  fontWeight="500"
                />

                <SingleLineText
                  sectionContent={findField('research-shows-decs')}
                  my="0"
                  mt={{ base: '4px', md: '12px', lg: '16px' }}
                  fontSize={{ base: '18px', md: '22px' }}
                  color="#52483D"
                  fontFamily="FedraSansStd-book"
                  lineHeight={{ base: '25.2px', md: '31.24px' }}
                />
                <Box pt={{ base: '27px', lg: '30px', xl: '38px' }}>
                  {findField('ie-wave-research-text-1')?.text && (
                    <Text
                      as="span"
                      my="0"
                      fontSize={{ base: '20px', md: '24px' }}
                      lineHeight={{ base: '26px', md: '30.4px' }}
                      color="#12166F"
                      fontFamily="FedraSansStd-medium"
                      fontWeight="500"
                    >
                      {findField('ie-wave-research-text-1')?.text}{' '}
                    </Text>
                  )}
                  <Text
                    as="span"
                    my="0"
                    fontSize={{ base: '20px', md: '24px' }}
                    lineHeight={{ base: '26px', md: '30.4px' }}
                    color="#F37021"
                    fontFamily="FedraSansStd-medium"
                    fontWeight="500"
                    textDecoration="underline"
                    cursor={'pointer'}
                    onClick={onOpen}
                  >
                    {sectionContent?.[5]?.text}
                    {findField('ie-wave-research-popup')?.text}
                  </Text>
                  <Text
                    as="span"
                    my="0"
                    fontSize={{ base: '20px', md: '24px' }}
                    lineHeight={{ base: '24px', md: '28.4px' }}
                    color="#12166F"
                    fontFamily="FedraSansStd-medium"
                    fontWeight="500"
                  >
                    {' '}
                    {findField('ie-wave-research-text-2')?.text}{' '}
                  </Text>
                </Box>
                {sectionContent?.[8]?.body && (
                  <StructuredTextParser
                    onClick={onOpen}
                    str={render(sectionContent[8].body)}
                    my="0"
                    ref={btnRef}
                    pt={{ base: '12px', md: '1px' }}
                    mt="0px"
                    mx="auto"
                    fontSize={{ base: '18px', md: '22px' }}
                    className="research_extra_shows"
                    lineHeight={{ base: '25.2px', md: '31.24px' }}
                    color="#52483D"
                    fontFamily="FedraSansStd-book"
                  />
                )}
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Box>
      <IeoBenefitsV8Modal
        isOpen={isOpen}
        onClose={onClose}
        btnRef={btnRef}
        sectionContent={sectionContent}
        lang={lang}
      />
      <style jsx global>
        {`
          .with-sadhguru-sub-text p:first-child {
            padding-bottom: 8px;
          }
          .research_extra_shows strong {
            font-size: 20px !important;
            color: #12166f !important;
            font-family: 'FedraSansStd-medium' !important;
            font-weight: 500 !important;
          }
          .research_extra_shows ul {
            margin-left: 30px;
          }
          @media (width >= 768px) {
            .with-sadhguru-sub-text p:first-child {
              padding-bottom: 8px;
            }

            .research_extra_shows ul p {
              margin-top: 10px;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default ResearchIntro;
